import type { Dataset, RowTypes, TableName } from './schema.ts';

export interface Put<T extends TableName = TableName> { table: T; row: RowTypes[T] }
export interface Del { table: TableName; id: string }
export interface Changeset {
  puts: Put[];
  deletes: Del[];
  /** human/audit description of the command that produced the change */
  action: string;
  summary?: string;
  entity?: TableName;
  entityId?: string;
  /** sync only: base version to send for a row (conflict resolution against a newer server version) */
  syncBase?: Record<string, number>;
  /** v13: server step-up grants to send with the sync operation of a row (row id → signed grant) */
  grants?: Record<string, string>;
  /** v13: not offered for undo (a secured action is reversed only through its own secured flow) */
  noUndo?: boolean;
}

/** Read-only view of the workspace used by pure domain services. */
export interface WorkspaceView {
  /** the rows in use: soft-deleted policies and the records that depend on them are left out (v13) */
  rows<T extends TableName>(table: T): readonly RowTypes[T][];
  /** by id, including a soft-deleted policy (so a linked record can say so); callers check `deletedAt` */
  get<T extends TableName>(table: T, id: string | null | undefined): RowTypes[T] | undefined;
  /** indexed lookup over `rows` (deleted policies and their dependants left out, as in rows) */
  where<T extends TableName>(table: T, field: keyof RowTypes[T], value: unknown): RowTypes[T][];
  /** every stored row, deleted ones included (sync seeding, backups, GDPR export, anonymisation) */
  allRows?<T extends TableName>(table: T): readonly RowTypes[T][];
}

/**
 * v13 «Διαγραφή λανθασμένου συμβολαίου»: tables whose rows exist only because of one policy (`policyId`). When that
 * policy is soft-deleted they are hidden with it — kept in storage, never orphaned, and back on restore. Cases
 * (the application that led to the policy), sale files (the IDD record of the sale) and the timeline are history
 * of what happened and stay visible; the policy itself is shown there as deleted.
 */
export const POLICY_DEPENDENT_TABLES: readonly TableName[] = ['policy_adjustments', 'tasks', 'documents', 'service_cases', 'claims', 'production_events', 'renewals', 'commission_entries', 'premium_arrears'];
const DEPENDENT = new Set<string>(POLICY_DEPENDENT_TABLES);
/** every stored row of a table, soft-deleted policies included (falls back to rows() for simple views) */
export function allRowsOf<T extends TableName>(v: WorkspaceView, table: T): readonly RowTypes[T][] { return v.allRows ? v.allRows(table) : v.rows(table); }

/** Simple indexed view over a Dataset (used by services, tests, importer and UI). */
export class DatasetView implements WorkspaceView {
  private byId = new Map<TableName, Map<string, unknown>>();
  private idx = new Map<string, Map<unknown, unknown[]>>();
  private visible = new Map<TableName, unknown[]>();
  private deleted: Set<string> | null = null;
  readonly data: Dataset;
  constructor(data: Dataset) { this.data = data; }
  /** ids of soft-deleted policies (v13) */
  deletedPolicyIds(): ReadonlySet<string> {
    if (!this.deleted) this.deleted = new Set(((this.data.policies ?? []) as { id: string; deletedAt?: string | null }[]).filter((p) => !!p.deletedAt).map((p) => p.id));
    return this.deleted;
  }
  allRows<T extends TableName>(table: T): readonly RowTypes[T][] { return (this.data[table] ?? []) as RowTypes[T][]; }
  rows<T extends TableName>(table: T): readonly RowTypes[T][] {
    const all = (this.data[table] ?? []) as RowTypes[T][];
    if (table !== 'policies' && !DEPENDENT.has(table)) return all;
    let out = this.visible.get(table) as RowTypes[T][] | undefined;
    if (!out) {
      const del = this.deletedPolicyIds();
      out = !del.size ? all : table === 'policies'
        ? all.filter((r) => !(r as { deletedAt?: string | null }).deletedAt)
        : all.filter((r) => { const pid = (r as { policyId?: string | null }).policyId; return !pid || !del.has(pid); });
      this.visible.set(table, out);
    }
    return out;
  }
  get<T extends TableName>(table: T, id: string | null | undefined): RowTypes[T] | undefined {
    if (!id) return undefined;
    let m = this.byId.get(table);
    if (!m) { m = new Map((this.data[table] as { id: string }[]).map((r) => [r.id, r])); this.byId.set(table, m); }
    return m.get(id) as RowTypes[T] | undefined;
  }
  where<T extends TableName>(table: T, field: keyof RowTypes[T], value: unknown): RowTypes[T][] {
    const key = `${table}.${String(field)}`;
    let m = this.idx.get(key);
    if (!m) {
      m = new Map();
      for (const r of this.rows(table) as unknown as Record<string, unknown>[]) {
        const v = r[field as string];
        const list = m.get(v) ?? [];
        list.push(r);
        m.set(v, list);
      }
      this.idx.set(key, m);
    }
    return (m.get(value) ?? []) as RowTypes[T][];
  }
}

/** Apply a changeset to a dataset immutably (per-table copy-on-write). */
export function applyChangeset(ds: Dataset, cs: Changeset): Dataset {
  const next = { ...ds } as Record<TableName, unknown[]>;
  const touched = new Map<TableName, Map<string, unknown>>();
  const tableMap = (t: TableName) => {
    let m = touched.get(t);
    if (!m) { m = new Map((ds[t] as { id: string }[]).map((r) => [r.id, r])); touched.set(t, m); }
    return m;
  };
  for (const d of cs.deletes) tableMap(d.table).delete(d.id);
  for (const p of cs.puts) tableMap(p.table).set((p.row as { id: string }).id, p.row);
  for (const [t, m] of touched) next[t] = [...m.values()];
  return next as Dataset;
}

export function mergeChangesets(action: string, ...parts: Changeset[]): Changeset {
  return { action, puts: parts.flatMap((p) => p.puts), deletes: parts.flatMap((p) => p.deletes), summary: parts.map((p) => p.summary).filter(Boolean).join(' · ') || undefined };
}
